import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { dataDeHoje } from '../lib/format';
import { useT } from '../lib/i18n';
import '../styles/calendario.css';

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;
const MES_CURTO = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
] as const;
const SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;
/** Altura do painel. Serve para decidir se ele cabe abaixo do campo. */
const ALTURA = 330;

/**
 * Datas de calendario aqui sao SEMPRE 'AAAA-MM-DD' manipuladas em UTC.
 *
 * `new Date('2026-08-24')` e meia-noite UTC; formatar em Brasilia devolve 23/08.
 * Todo calculo abaixo usa `Date.UTC` e os getters `getUTC*` pelo mesmo motivo.
 */
function partes(iso: string): { ano: number; mes: number; dia: number } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [a, m, d] = iso.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(a, m - 1, d));
  if (t.getUTCFullYear() !== a || t.getUTCMonth() !== m - 1 || t.getUTCDate() !== d) return null;
  return { ano: a, mes: m - 1, dia: d };
}

/** 'AAAA-MM-DD' → '24/08/2026'. Vazio continua vazio. */
function paraBr(iso: string): string {
  const p = partes(iso);
  if (p === null) return '';
  return `${String(p.dia).padStart(2, '0')}/${String(p.mes + 1).padStart(2, '0')}/${p.ano}`;
}

/** '24/08/2026' → 'AAAA-MM-DD'. Devolve null enquanto estiver incompleta. */
function deBr(br: string): string | null {
  const n = br.replace(/\D/g, '');
  if (n.length !== 8) return null;
  const iso = `${n.slice(4)}-${n.slice(2, 4)}-${n.slice(0, 2)}`;
  return partes(iso) === null ? null : iso;
}

/** Vai formatando enquanto digita, sem nunca inserir o que o dedo nao pediu. */
function mascarar(bruto: string): string {
  const n = bruto.replace(/\D/g, '').slice(0, 8);
  if (n.length <= 2) return n;
  if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
  return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4)}`;
}

/** As 42 celulas da grade: o mes cheio, ladeado pelas pontas dos vizinhos. */
function celulas(ano: number, mes: number): readonly { iso: string; doMes: boolean }[] {
  const primeiro = new Date(Date.UTC(ano, mes, 1)).getUTCDay();
  const saida: { iso: string; doMes: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(Date.UTC(ano, mes, 1 - primeiro + i));
    saida.push({ iso: d.toISOString().slice(0, 10), doMes: d.getUTCMonth() === mes });
  }
  return saida;
}

function foraDaFaixa(iso: string, min?: string, max?: string): boolean {
  if (min !== undefined && min !== '' && iso < min) return true;
  if (max !== undefined && max !== '' && iso > max) return true;
  return false;
}

/**
 * O calendario, ancorado em qualquer gatilho.
 *
 * O gatilho vem de fora porque ele muda: no campo de data e um icone dentro da
 * caixa, e no painel de marcar e o proprio rotulo do dia. Um componente que
 * desenhasse os dois viraria uma arvore de props de aparencia.
 */
export function CalendarioPopover({
  value,
  onChange,
  min,
  max,
  limpavel = false,
  alvo,
}: {
  readonly value: string;
  readonly onChange: (iso: string) => void;
  readonly min?: string;
  readonly max?: string;
  readonly limpavel?: boolean;
  readonly alvo: (abrir: () => void, aberto: boolean) => ReactNode;
}): ReactElement {
  const t = useT();
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ x: number; y: number; acima: boolean } | null>(null);
  const [vista, setVista] = useState<'dias' | 'meses' | 'anos'>('dias');
  const base = partes(value) ?? (partes(dataDeHoje()) as { ano: number; mes: number; dia: number });
  const [ano, setAno] = useState(base.ano);
  const [mes, setMes] = useState(base.mes);
  const caixa = useRef<HTMLDivElement>(null);
  const painel = useRef<HTMLDivElement>(null);
  const id = useId();

  function abrir(): void {
    if (aberto) {
      setAberto(false);
      return;
    }
    // O gatilho, e nao o embrulho: `.cal__ancora` e `display: contents` para nao
    // atrapalhar o layout de quem a usa, e um elemento assim nao gera caixa —
    // medi-lo devolve zeros, e o painel nascia no canto da tela.
    const alvoReal = caixa.current?.firstElementChild ?? caixa.current;
    const r = alvoReal?.getBoundingClientRect();
    if (r === undefined || r.width === 0) return;
    const p = partes(value) ?? (partes(dataDeHoje()) as { ano: number; mes: number; dia: number });
    setAno(p.ano);
    setMes(p.mes);
    setVista('dias');
    // Perto do rodape o painel sobe. Um campo dentro de linha de tabela quase
    // sempre esta la embaixo, e abrindo para baixo ele nasceria cortado.
    const cabeAbaixo = window.innerHeight - r.bottom > ALTURA;
    setPos({
      x: Math.min(r.left, window.innerWidth - 256),
      y: cabeAbaixo ? r.bottom + 6 : r.top - 6,
      acima: !cabeAbaixo,
    });
    setAberto(true);
  }

  useEffect(() => {
    if (!aberto) return;
    function noDocumento(e: MouseEvent): void {
      const alvoDoClique = e.target as Node;
      if (painel.current?.contains(alvoDoClique) === true) return;
      if (caixa.current?.contains(alvoDoClique) === true) return;
      setAberto(false);
    }
    function naTecla(e: KeyboardEvent): void {
      if (e.key === 'Escape') setAberto(false);
    }
    function aoRolar(): void {
      setAberto(false);
    }
    document.addEventListener('mousedown', noDocumento, true);
    document.addEventListener('keydown', naTecla);
    window.addEventListener('scroll', aoRolar, true);
    return () => {
      document.removeEventListener('mousedown', noDocumento, true);
      document.removeEventListener('keydown', naTecla);
      window.removeEventListener('scroll', aoRolar, true);
    };
  }, [aberto]);

  const grade = useMemo(() => celulas(ano, mes), [ano, mes]);
  const hoje = dataDeHoje();
  const decada = Math.floor(ano / 12) * 12;

  function escolher(iso: string): void {
    onChange(iso);
    setAberto(false);
  }

  function anterior(): void {
    if (vista === 'dias') {
      if (mes === 0) {
        setMes(11);
        setAno(ano - 1);
      } else setMes(mes - 1);
    } else if (vista === 'meses') setAno(ano - 1);
    else setAno(ano - 12);
  }

  function proximo(): void {
    if (vista === 'dias') {
      if (mes === 11) {
        setMes(0);
        setAno(ano + 1);
      } else setMes(mes + 1);
    } else if (vista === 'meses') setAno(ano + 1);
    else setAno(ano + 12);
  }

  return (
    <>
      <div ref={caixa} className="cal__ancora">
        {alvo(abrir, aberto)}
      </div>

      {!aberto || pos === null
        ? null
        : createPortal(
            <div
              ref={painel}
              id={id}
              className={`cal${pos.acima ? ' cal--acima' : ''}`}
              role="dialog"
              aria-label={t.ui.calendario}
              style={{ left: pos.x, top: pos.y }}
            >
              <div className="cal__topo">
                <button
                  type="button"
                  className="cal__seta"
                  onClick={anterior}
                  aria-label={t.ui.anterior}
                >
                  <Icon name="chevron-left" size={14} />
                </button>
                {/* O titulo sobe de nivel: mes → ano → decada. Sem isto, chegar
                    numa data de nascimento sao centenas de cliques na seta. */}
                <button
                  type="button"
                  className="cal__titulo"
                  onClick={() =>
                    setVista(vista === 'dias' ? 'meses' : vista === 'meses' ? 'anos' : 'dias')
                  }
                >
                  {vista === 'dias'
                    ? `${MESES[mes]} ${ano}`
                    : vista === 'meses'
                      ? ano
                      : `${decada}–${decada + 11}`}
                </button>
                <button
                  type="button"
                  className="cal__seta"
                  onClick={proximo}
                  aria-label={t.ui.proximo}
                >
                  <Icon name="chevron-right" size={14} />
                </button>
              </div>

              {vista === 'dias' ? (
                <>
                  <div className="cal__semana">
                    {SEMANA.map((d, i) => (
                      <span key={i}>{d}</span>
                    ))}
                  </div>
                  <div className="cal__grade">
                    {grade.map((c) => (
                      <button
                        key={c.iso}
                        type="button"
                        className={`cal__dia${c.doMes ? '' : ' is-fora'}${c.iso === hoje ? ' is-hoje' : ''}${c.iso === value ? ' is-on' : ''}`}
                        disabled={foraDaFaixa(c.iso, min, max)}
                        onClick={() => escolher(c.iso)}
                      >
                        {Number(c.iso.slice(8))}
                      </button>
                    ))}
                  </div>
                </>
              ) : vista === 'meses' ? (
                <div className="cal__meses">
                  {MES_CURTO.map((m, i) => (
                    <button
                      key={m}
                      type="button"
                      className={`cal__celula${i === mes ? ' is-on' : ''}`}
                      onClick={() => {
                        setMes(i);
                        setVista('dias');
                      }}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="cal__meses">
                  {Array.from({ length: 12 }, (_, i) => decada + i).map((a) => (
                    <button
                      key={a}
                      type="button"
                      className={`cal__celula${a === ano ? ' is-on' : ''}`}
                      onClick={() => {
                        setAno(a);
                        setVista('meses');
                      }}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              )}

              <div className="cal__pe">
                <button
                  type="button"
                  className="cal__atalho"
                  onClick={() => escolher(hoje)}
                  disabled={foraDaFaixa(hoje, min, max)}
                >
                  {t.ui.hoje}
                </button>
                {limpavel ? (
                  <button
                    type="button"
                    className="cal__atalho"
                    onClick={() => {
                      onChange('');
                      setAberto(false);
                    }}
                  >
                    {t.ui.limpar}
                  </button>
                ) : null}
              </div>
            </div>,
            document.body,
          )}
    </>
  );
}

/**
 * Campo de data do Zenda.
 *
 * ── Por que nao o `<input type="date">` ──
 * O calendario nativo e desenhado pelo navegador: cores, raio e tipografia dele,
 * diferentes em cada sistema, e nenhum deles parecido com o resto do produto.
 *
 * ── Por que continua dando para DIGITAR ──
 * Data de nascimento e o caso que decide isto: chegar em 1957 clicando na seta
 * do mes sao centenas de cliques. O campo aceita "15081957" e formata sozinho; o
 * calendario e para quando a pessoa esta ESCOLHENDO uma data, e nao
 * transcrevendo uma que ja sabe.
 */
export function CampoData({
  value,
  onChange,
  min,
  max,
  size = 'md',
  id,
  placeholder = 'dd/mm/aaaa',
  limpavel = true,
  className = '',
  'aria-label': ariaLabel,
}: {
  /** 'AAAA-MM-DD', ou vazio. */
  readonly value: string;
  readonly onChange: (iso: string) => void;
  readonly min?: string;
  readonly max?: string;
  readonly size?: 'md' | 'sm' | 'xs';
  readonly id?: string;
  readonly placeholder?: string;
  /** Um botao para esvaziar. Desligue quando a data for obrigatoria. */
  readonly limpavel?: boolean;
  readonly className?: string;
  readonly 'aria-label'?: string;
}): ReactElement {
  const t = useT();
  const [texto, setTexto] = useState(() => paraBr(value));

  // O valor pode mudar por fora — o servidor responde, o formulario reseta.
  useEffect(() => {
    setTexto(paraBr(value));
  }, [value]);

  function digitou(bruto: string): void {
    const t = mascarar(bruto);
    setTexto(t);
    if (t === '') {
      onChange('');
      return;
    }
    const iso = deBr(t);
    if (iso !== null && !foraDaFaixa(iso, min, max)) onChange(iso);
  }

  /** Ao sair do campo, o que nao virou data volta a ser o valor bom. */
  function saiu(): void {
    if (texto === '') return;
    const iso = deBr(texto);
    if (iso === null || foraDaFaixa(iso, min, max)) setTexto(paraBr(value));
  }

  return (
    <CalendarioPopover
      value={value}
      onChange={onChange}
      {...(min === undefined ? {} : { min })}
      {...(max === undefined ? {} : { max })}
      limpavel={limpavel}
      alvo={(abrir, aberto) => (
        <div className={`cd cd--${size}${aberto ? ' is-open' : ''} ${className}`.trim()}>
          <input
            id={id}
            type="text"
            inputMode="numeric"
            className="cd__input"
            value={texto}
            placeholder={placeholder}
            aria-label={ariaLabel}
            onChange={(e) => digitou(e.target.value)}
            onBlur={saiu}
          />
          {limpavel && texto !== '' ? (
            <button
              type="button"
              className="cd__acao"
              onClick={() => {
                setTexto('');
                onChange('');
              }}
              aria-label={t.ui.limparData}
            >
              <Icon name="x" size={12} />
            </button>
          ) : null}
          <button
            type="button"
            className="cd__acao"
            onClick={abrir}
            aria-haspopup="dialog"
            aria-expanded={aberto}
            aria-label={t.ui.abrirCalendario}
          >
            <Icon name="calendar" size={14} />
          </button>
        </div>
      )}
    />
  );
}
